/**
 * File download utilities for handling various download scenarios
 * Centralizes common download patterns used throughout the application
 */

/**
 * Internal helper to trigger download with proper DOM manipulation
 */
function triggerDownload(
  url: string,
  filename?: string,
  cleanup?: () => void
): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Delay cleanup to avoid interrupting download in some browsers
  if (cleanup) {
    setTimeout(cleanup, 100);
  }
}

/**
 * Downloads a file directly from a URL using an anchor element
 */
export function downloadFromUrl(url: string, filename?: string): void {
  if (!url) {
    console.warn("downloadFromUrl: URL is required");
    return;
  }

  triggerDownload(url, filename);
}

/**
 * Downloads data as a file with proper object URL management
 * Intelligently handles existing Blobs vs raw data to avoid double-wrapping
 */
export function downloadFromBlob(
  data: Blob | string | ArrayBuffer | any,
  filename: string,
  mimeType?: string
): void {
  if (!data) {
    throw new Error("downloadFromBlob: Data is required");
  }
  if (!filename) {
    throw new Error("downloadFromBlob: Filename is required");
  }

  let fileBlob: Blob;

  // Handle different input types
  if (data instanceof Blob) {
    // If it's already a Blob, only re-wrap if we need to override the MIME type
    // and it's different from the existing type
    if (mimeType && mimeType !== data.type) {
      fileBlob = new Blob([data], { type: mimeType });
    } else {
      fileBlob = data;
    }
  } else {
    // For raw data, always create a new Blob
    fileBlob = new Blob([data], { type: mimeType });
  }

  const fileURL = window.URL.createObjectURL(fileBlob);

  triggerDownload(fileURL, filename, () => {
    // Clean up object URL after a delay to prevent interrupting download
    window.URL.revokeObjectURL(fileURL);
  });
}

/**
 * Extracts filename from HTTP Content-Disposition header
 * Supports RFC 5987/6266 extended format with percent-decoding
 */
export function extractFilenameFromContentDisposition(
  contentDisposition?: string
): string | null {
  if (!contentDisposition) {
    return null;
  }

  // First try RFC 5987 extended format: filename*=charset'lang'encoded-value
  const extendedMatch = contentDisposition.match(
    /filename\*=([^']*)'[^']*'([^;]+)/i
  );
  if (extendedMatch) {
    try {
      const encodedValue = extendedMatch[2];
      // Decode percent-encoded characters
      return decodeURIComponent(encodedValue);
    } catch (error) {
      console.warn("Failed to decode extended filename:", error);
      // Continue to fallback
    }
  }

  // Fallback to basic format: filename=value
  const basicMatch = contentDisposition.match(/filename=([^;]+)/i);
  if (basicMatch) {
    let filename = basicMatch[1].trim();

    // Remove surrounding quotes if present (double or single)
    if (
      (filename.startsWith('"') && filename.endsWith('"')) ||
      (filename.startsWith("'") && filename.endsWith("'"))
    ) {
      filename = filename.slice(1, -1);
    }

    return filename.trim();
  }

  return null;
}

/**
 * Extracts filename from a URL path
 * Strips query parameters and returns "Document" if extraction fails
 */
export function extractFilenameFromUrl(url: string): string {
  if (!url) {
    return "Document";
  }

  try {
    const urlParts = url.split("/");
    const lastPart = urlParts[urlParts.length - 1];

    // Handle URLs with query parameters
    const filename = lastPart.split("?")[0];

    return filename || "Document";
  } catch (error) {
    console.warn("extractFilenameFromUrl: Error parsing URL", error);
    return "Document";
  }
}

/**
 * Downloads files from API responses with automatic filename extraction
 * Tries Content-Disposition header first, falls back to provided filename
 * Handles responseType: "blob" responses without double-wrapping
 */
export function downloadFromApiResponse(
  responseData: any,
  headers: Record<string, any>,
  fallbackFilename: string,
  mimeType?: string
): void {
  if (!responseData) {
    throw new Error("downloadFromApiResponse: Response data is required");
  }
  if (!fallbackFilename) {
    throw new Error("downloadFromApiResponse: Fallback filename is required");
  }

  // Try to extract filename from Content-Disposition header
  const contentDisposition =
    headers["content-disposition"] || headers["Content-Disposition"];
  let filename = extractFilenameFromContentDisposition(contentDisposition);

  // Use fallback if extraction failed
  if (!filename) {
    filename = fallbackFilename;
  }

  // Determine mime type from headers if not provided
  const responseMimeType =
    mimeType || headers["content-type"] || headers["Content-Type"];

  // Pass responseData directly to downloadFromBlob
  // (responseData is already a Blob when using responseType: "blob")
  downloadFromBlob(responseData, filename, responseMimeType);
}

/**
 * Sanitizes a string for use in a filename by removing illegal characters
 * and replacing spaces with underscores
 */
export function sanitizeFilenameComponent(input: string): string {
  if (!input) {
    return "";
  }

  // Replace spaces with underscores
  let sanitized = input.replace(/\s+/g, "_");

  // Remove characters that are illegal in filenames on Windows/Mac/Linux
  // Illegal chars: < > : " / \ | ? *
  // Also remove periods to avoid confusion with file extensions
  // Also remove control characters (0x00-0x1F)
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[<>:"/\\|?*.\x00-\x1F]/g, "");

  // Remove leading/trailing underscores and dots
  sanitized = sanitized.replace(/^[_.]+|[_.]+$/g, "");

  // Collapse multiple consecutive underscores into one
  sanitized = sanitized.replace(/_+/g, "_");

  return sanitized;
}

/**
 * Extracts the file extension from a filename or URL
 */
export function getFileExtension(filename: string): string {
  if (!filename) {
    return "";
  }

  // Remove query parameters if present
  let cleanFilename = filename.split("?")[0];

  // If it looks like a URL, extract just the path's last segment
  if (cleanFilename.includes("://")) {
    try {
      const url = new URL(cleanFilename);
      cleanFilename = url.pathname.split("/").pop() || "";
    } catch {
      // If URL parsing fails, try to extract the last path segment manually
      const lastSlash = cleanFilename.lastIndexOf("/");
      if (lastSlash !== -1) {
        cleanFilename = cleanFilename.substring(lastSlash + 1);
      }
    }
  }

  const lastDot = cleanFilename.lastIndexOf(".");
  if (lastDot === -1 || lastDot === cleanFilename.length - 1) {
    return "";
  }

  return cleanFilename.substring(lastDot + 1).toLowerCase();
}

// Re-export for backwards compatibility and convenience
export const downloadUtils = {
  downloadFromUrl,
  downloadFromBlob,
  extractFilenameFromContentDisposition,
  extractFilenameFromUrl,
  downloadFromApiResponse,
  sanitizeFilenameComponent,
  getFileExtension,
} as const;

export default downloadUtils;
